import React from "react";
import logoSmall from "@/assets/images/logo-small.svg";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { APP_STRIP_WIDTH } from "@/components/ui/sidebar";
import { AppStripItemConfig, AppStripId } from "./appStripTypes";
import AppStripItem from "./AppStripItem";
import AppStripItemWithMenu from "./AppStripItemWithMenu";
import { useAppStripItems } from "./useAppStripItems";

export { flattenNavigationItems, isAppStripItemVisible } from "./visibility";

interface AppStripProps {
  onAppClick?: (appId: AppStripId) => void;
}

export default function AppStrip({ onAppClick }: AppStripProps) {
  const { activeApp, visibleItems, openApp } = useAppStripItems();
  const handleItemClick = (item: AppStripItemConfig) =>
    openApp(item, onAppClick);

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "flex h-full flex-shrink-0 flex-col items-center overflow-hidden",
          "bg-white dark:bg-slate-900",
          "border-r border-gray-200 dark:border-slate-800/50",
          "py-3",
        )}
        data-appstrip
        style={{ width: APP_STRIP_WIDTH }}
      >
        {/* Logo at top */}
        <div className="mb-4 flex h-10 w-10 items-center justify-center">
          <img alt="CertainPath" className="h-8 w-8" src={logoSmall} />
        </div>

        {/* Separator */}
        <div className="mb-3 h-px w-8 bg-slate-700/50" />

        {/* App icons */}
        <nav className="flex w-full flex-1 flex-col gap-0.5 px-1">
          {visibleItems.map((item) =>
            item.hasMenu ? (
              <AppStripItemWithMenu
                isActive={activeApp === item.id}
                item={item}
                key={item.id}
                onMenuItemClick={() => onAppClick?.(item.id as AppStripId)}
              />
            ) : (
              <AppStripItem
                isActive={!item.isExternal && activeApp === item.id}
                item={item}
                key={item.id}
                onClick={() => handleItemClick(item)}
              />
            ),
          )}
        </nav>
      </div>
    </TooltipProvider>
  );
}
